<template>
  <div class="enhancify grid-module"
       v-if="hasAccess(REPORTS_COMMISSIONS_READ, REPORTS_COMMISSIONS_READ_MANAGE)">
    <div class="header">
      <h1>Commissions</h1>
      <div
          v-if="hasAccess(PAYOUT_TAX_ACCESS, PAYOUT_TAX_ACCESS_MANAGE)
          && (!payoutMethod || w9Status !== 'Verified') && !loading"
          class="notification-group"
      >
        <b-notification
            :closable=false
            type="alert-block"
            message="Your payouts will be held by Enhancify until the required ACH Deposit
        and W-9 Tax form is completed."
        />
        <div class="notification-group-set-up-group">
          <router-link class="notification-group-ref"
                       :to="{ name: 'payout-tax' }">GET SET UP</router-link>
        </div>
      </div>
      <div>
        <b-notification
          :closable=false
          type="notification-block"
          message="Commissions are calculated each month for referred customers who have been using
          Enhancify services for at least one month and haven’t cancelled their subscription.<br>
          All pending commissions are paid once a month during the next pay period, after you have
          correctly filled out the ACH Info & W-9 form.<br>
          The pay period runs from the 21st to the 25th of each month. It can take several days to
          process the transaction.<br>
          For your security, Enhancify pays all commissions with a direct deposit via Bill.com."
        />
      </div>
      <div class="grid-module__filter">
        <div class="modern-form-group sm-modern-form-group">
          <div class="floating-button">
            <b-button
                class="button-right sm-show"
                :disabled="btnExportsDisabled"
                @click="onExportClick"
                type="outlined"
                position="is-right"
                style="min-width: 160px; margin: auto">
              EXPORT
            </b-button>
          </div>
        </div>
        <div class="modern-form-group" >
          <div class="modern-input-labeled" v-on:keyup.enter="doSearch">
            <label for="applicantFilter" class="hide-label">Customer</label>
            <div class="modern-input-controlled">
              <ModernInput id="applicantFilter"
                           type="search"
                           placeholder="Search by customer"
                           v-model.trim="filters.searchText"/>
              <div class="modern-input-control">
                <button class="modern-button" :disabled="isFormDisabled" @click="doSearch" >
                  <i class="mdi mdi-magnify"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="modern-input-labeled">
            <label for="dateFilter">Date</label>
            <ModernDateSelect
                :disabled="isFormDisabled"
                id="dateFilter"
                ref="modernDateSelect"
                v-model="dateFilter"
                :minDate="minDate"
                :maxDate="maxDate"
                :options="dateFilterOptions"
            />
          </div>
          <div class="floating-button">
            <b-button
                class="button-right sm-hide"
                :disabled="btnExportsDisabled"
                @click="onExportClick"
                type="outlined"
                position="is-right"
                style="min-width: 160px; margin: auto">
              EXPORT
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <section class="data-grid">
      <template v-if="!data.length">
        <b-table
            class="modern-table"
            height="100%"
            :columns="columns"
            :loading="loading">
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']">
              {{ column['label'] }}
              <reports-table-tooltip
                  v-if="help[column['field']]"
                  :content="help[column['field']]" />
            </div>
          </template>
          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </b-table>
      </template>
      <template v-else>
        <ModernTable
            class="modern-table"
            :loading="loading"
            :per-page="filters.limit"
            :total="total"
            :current-page.sync="filters.page"
            :data="data"
            paginated
            hoverable
            pagination-size="is-medium"
            backend-pagination
            backend-sorting
            :defaultSort="filters.sortField"
            @sort="onSort"
        >
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']"
                 :ref="column['field']">
              {{ column['label'] }}
            </div>
          </template>
          <template #default="{ row }">
            <b-table-column label="Customer"
                            field="customer"
                            width="350"
                            class="report-table__column"
                            sticky sortable>
              <template>
                <span :class="[{
                  'report-table__value_fixed': windowWidth < 769
                }]">
                   <div v-if="row['companyName']">
                     <div><b>{{ row['userName'].trim() || '-'}}</b></div>
                     <div>{{ row['companyName'].trim() }}</div>
                   </div>
                   <div v-else>
                     <div><b>{{ row['userName'].trim() || '-'}}</b></div>
                   </div>
                </span>
              </template>
            </b-table-column>
            <b-table-column label="Date"
                            field="date"
                            width="220"
                            class="report-table__column"
                            sortable>
              <template #header >
                <span class="report-table__header"
                      v-html="'Date'" />
                <reports-table-tooltip :content="help['date']" />
              </template>
              <template>
                <reports-table-tooltip placement="auto"
                                       v-if="windowWidth < 769"
                                       :content="help['date']" />
                <span :class="[{
                  'report-table__value_fixed': windowWidth < 769
                }]">
                   {{ row['date'] }}
                </span>
              </template>
            </b-table-column>
            <b-table-column label="Commission"
                            field="commission"
                            width="400"
                            class="report-table__column"
                            sortable>
              <template #header >
                <span class="report-table__header"
                      v-html="'Commission'" />
                <reports-table-tooltip :content="help['commission']" />
              </template>
              <template>
                <reports-table-tooltip
                        placement="auto"
                        v-if="windowWidth < 769"
                        :content="help['commission']" />
                <span
                  class="report-table__commission"
                  :class="[{
                    'report-table__value_fixed': windowWidth < 769
                }]">
                   {{ row['commission'] | currency(2)}}
                </span>
              </template>
            </b-table-column>
            <b-table-column label=""
                            field="type"
                            width="400"
                            class="report-table__column"
                            sortable>
              <template>
                <span :class="[{
                  'report-table__value_fixed': windowWidth < 769
                }]">
                </span>
              </template>
            </b-table-column>
          </template>
          <template #bottom-left-left>
              <b>Commission (Total): {{ commissionTotal | currency(2) }}</b>
          </template>
          <template #bottom-left>
            <div class="modern-input-labeled">
              <label class="per-page"
                     style="font-weight: 700; white-space: nowrap">
                Per page
              </label>
              <ModernSelect
                style="margin-right: 1em"
                v-model="filters.limit"
                :options="perPageOptions"
              />
            </div>
          </template>
        </ModernTable>
      </template>
    </section>
    <div class="grid-module__footer table-note">
      <p>
        * This report contains information on commissions earned over the past 2 years.
      </p>
    </div>
  </div>
  <div v-else>
    <p class="denied-report">
      This report is not available in your Plan. Please contact your manager.
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { commissions } from '@/api/report_applications';
import { partnerPayoutTax } from '@/api/partner';
import * as PARTNER_ACTIONS from '@/store/modules/partner/actions';
import { isEmpty } from 'lodash';
import CommissionReportHelp from '@/assets/CommissionReportPartnerHelp';
import ReportsTableTooltip from '@/components/Widgets/ReportsTableTooltip.vue';
import ModernDateSelect from '@/components/ModernDateSelect.vue';
import { RANGE } from '@/const/constants';
import { getRange } from '@/utils/range';
import ModernInput from '@/components/ModernInput.vue';
import ModernSelect from '@/components/ModernSelect.vue';
import ModernTable from '@/components/ModernTable.vue';

export default {
  name: 'ReportsCommissionsPartner',
  components: {
    ReportsTableTooltip,
    ModernDateSelect,
    ModernSelect,
    ModernInput,
    ModernTable,
  },
  data() {
    const today = new Date();
    return {
      isTopSticky: false,
      filters: {
        searchText: '',
        page: 1,
        limit: 25,
        sortField: 'date',
        sortOrder: 'desc',
      },
      dateFilter: { selectedRange: RANGE.LAST_2_MONTHS, ...getRange(RANGE.LAST_2_MONTHS) },
      dateFilterOptions: [
        { value: RANGE.THIS_MONTH, label: 'This month' },
        { value: RANGE.LAST_MONTH, label: 'Last month' },
        { value: RANGE.LAST_2_MONTHS, label: 'Last 2 months' },
        { value: RANGE.LAST_3_MONTHS, label: 'Last 3 months' },
        { value: RANGE.LAST_6_MONTHS, label: 'Last 6 months' },
        { value: RANGE.THIS_YEAR, label: 'This year' },
        { value: RANGE.LAST_YEAR, label: 'Last year' },
        { value: RANGE.CUSTOM, label: 'Custom' },
      ],
      minDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 729),
      maxDate: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      w9Status: null,
      payoutMethod: null,
      data: [],
      isFormDisabled: false,
      btnExportsDisabled: true,
      type: '',
      reportType: 'partner',
      total: 0,
      loading: false,
      commissionTotal: 0,
      perPageOptions: [{ value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }],
      isDataEmpty: true,
      help: CommissionReportHelp,
      columns: [
        {
          field: 'customer',
          label: 'Customer',
          sticky: true,
          sortable: true,
          width: 300,
        },
        {
          field: 'date',
          label: 'Date',
          sortable: true,
          width: 200,
        },
        {
          field: 'commission',
          label: 'Commission',
          sortable: true,
          width: 400,
        },
        {
          field: 'type',
          label: '',
          sortable: true,
          width: 200,
        },
      ],
      REPORTS_COMMISSIONS_READ: { resource: 'reports-commissions', code: 'read' },
      REPORTS_COMMISSIONS_READ_MANAGE: { resource: 'reports-commissions', code: 'manage_read' },
      PAYOUT_TAX_ACCESS: { resource: 'payout-tax', code: 'access' },
      PAYOUT_TAX_ACCESS_MANAGE: { resource: 'payout-tax', code: 'manage_access' },
    };
  },
  watch: {
    watchFilters() {
      this.doSearch();
    },
    'filters.limit': function (newLimit, oldLimit) {
      if (newLimit === oldLimit) {
        return;
      }
      if (this.filters.page !== 1) {
        this.filters.page = 1;
      } else {
        this.doSearch();
      }
    },
  },

  activated() {
    this.doSearch();
  },

  computed: {
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['isSwitched', 'userAuthenticator', 'authAuthenticator', 'hasAccess']),
    ...mapState({ windowWidth: (state) => state.windowWidth }),
    ...mapState('partner', { partnerId: (state) => state.id }),
    watchFilters() {
      return [this.type, this.dateFilter, this.filters.page];
    },
  },


  methods: {
    getTypeTitle(row) {
      switch (row.type) {
        case 'referral': return 'Referral';
        case 'partner': return 'Partner';
        case 'credit_card': return '0% funding';
        case 'loan': return row.fundedAmount > 0 ? `Loans (${this.currency(row.fundedAmount, 0)})` : 'Loans';
        case 'heloc': return 'HELOC (per qualified lead)';
        default: return '';
      }
    },
    onExportClick() {
      const url = new URL('api/v1/reports-commissions-export', document.location.origin);
      url.searchParams.set('order', `${this.filters.sortOrder === 'desc' ? '-' : '+'}${this.filters.sortField}`);
      url.searchParams.set('userId', this.userId);
      url.searchParams.set('reportType', this.reportType);
      url.searchParams.set('filters', JSON.stringify({
        viewDate: {
          start: this.dateToString(this.dateFilter.from),
          end: this.dateToString(this.dateFilter.to),
        },
        searchText: this.filters.searchText,
        type: this.type,
      }));
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener noreferrer';
      a.target = '_self';
      a.click();
    },
    async loadAsyncData() {
      this.isFormDisabled = true;
      this.btnExportsDisabled = true;
      this.loading = true;
      this.isDataEmpty = false;
      this.payoutMethod = null;
      this.w9Status = null;
      const queryParams = {
        order: `${this.filters.sortOrder === 'desc' ? '-' : '+'}${this.filters.sortField}`,
        page: this.filters.page,
        limit: this.filters.limit,
        userId: this.userId,
        reportType: this.reportType,
        filters: {
          viewDate: {
            start: this.dateToString(this.dateFilter.from),
            end: this.dateToString(this.dateFilter.to),
          },
          searchText: this.filters.searchText,
          type: this.type,
        },
      };
      if (
        this.partnerId
        && this.hasAccess(this.PAYOUT_TAX_ACCESS, this.PAYOUT_TAX_ACCESS_MANAGE)
      ) {
        await this.$store.dispatch(`partner/${PARTNER_ACTIONS.LOAD_W9}`);
        this.w9Status = this.$store.state.partner.w9.status;

        const response = await partnerPayoutTax(this.partnerId);
        if (!isEmpty(response)) {
          Object.keys(response.data).forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(this, [key])) {
              this[key] = response.data[key];
            }
          });
        }
      }

      try {
        const { data } = await commissions(queryParams);
        this.isFormDisabled = false;
        this.btnExportsDisabled = !(data.total > 0);
        this.commissionTotal = data.commissionTotal > 0
          ? data.commissionTotal : 0;
        this.data = data.data;
        this.isDataEmpty = this.data.length === 0;
        this.total = data.total;
        this.filters.page = data.page;
      } finally {
        this.loading = false;
      }
    },
    onSort(field, order) {
      this.filters.sortField = field;
      this.filters.sortOrder = order;
      this.loadAsyncData();
    },
    doSearch() {
      this.loadAsyncData();
    },

    /**
     * @param {Date} date
     * @returns {string}
     */
    dateToString(date) {
      return date.toLocaleString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
    },
    currency(amount, roundDigits) {
      if (typeof amount === 'undefined' || amount === null || amount === '') {
        return '';
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: roundDigits,
        maximumFractionDigits: roundDigits,
      });
      return formatter.format(amount);
    },
  },
};

</script>

<style scoped lang="stylus">
  @require "../../assets/style/modern/variables.styl";
  .report-table
    &__commission
      font-weight 700
    &__column_fix
      justify-content flex-start !important
    &__header
      color white
      font-weight 500
      font-size 18px
      @media (max-width: $breakpoints.lg)
        font-size 16px
        position relative
      .some
        position absolute
    &__icon
      position absolute
      color #525F66
      font-size 18px
      margin-left 3px
    &__value_fixed
      margin-left auto
      &.self-reported-credit-score-content
        text-align right
        @media (max-width: $breakpoints.md)
          width 100%

    &__column
      @media (min-width: $breakpoints.md) and (max-width: $breakpoints.lg)
        font-size 16px!important
      @media (max-width: $breakpoints.md)
        font-size 14px!important
  .notification-group
    margin-bottom 0
  @media (max-width: $breakpoints.lg)
    .sm-modern-form-group
      margin-bottom 0
      padding-bottom 0
    .hide-label
      display none
  .floating-button
    margin-left auto
    margin-right 0
  .sm-show
    display none
  @media (max-width: $breakpoints.md)
    .modern-form-group > *
      margin-right 0
    .sm-hide
      display none
    .sm-show
      display block
  .notification-group
    display flex
    align-items baseline
  .notification-group-ref
    padding-right 10px
    color #525F66
    font-weight bold
    position relative
  .notification-group-ref::before
    content url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='14' cy='14' r='13.5' fill='white' stroke='%23525F66'/%3E%3Cpath d='M20.3536 14.3536C20.5488 14.1583 20.5488 13.8417 20.3536 13.6464L17.1716 10.4645C16.9763 10.2692 16.6597 10.2692 16.4645 10.4645C16.2692 10.6597 16.2692 10.9763 16.4645 11.1716L19.2929 14L16.4645 16.8284C16.2692 17.0237 16.2692 17.3403 16.4645 17.5355C16.6597 17.7308 16.9763 17.7308 17.1716 17.5355L20.3536 14.3536ZM7 14.5H20V13.5H7V14.5Z' fill='%23525F66'/%3E%3C/svg%3E")
    position absolute
    top -3px
    left 100px
  .notification-group-ref:hover::before
    content url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='14' cy='14' r='13.5' fill='%23525F66' stroke='%23525F66'/%3E%3Cpath d='M20.3536 14.3536C20.5488 14.1583 20.5488 13.8417 20.3536 13.6464L17.1716 10.4645C16.9763 10.2692 16.6597 10.2692 16.4645 10.4645C16.2692 10.6597 16.2692 10.9763 16.4645 11.1716L19.2929 14L16.4645 16.8284C16.2692 17.0237 16.2692 17.3403 16.4645 17.5355C16.6597 17.7308 16.9763 17.7308 17.1716 17.5355L20.3536 14.3536ZM7 14.5H20V13.5H7V14.5Z' fill='white'/%3E%3C/svg%3E")
  .notification-group-set-up-group
    min-width 120px
</style>
